import React, { useEffect, useState, useRef, useCallback } from "react";
import { X, Download, Loader2, CheckCircle2, AlertCircle, Package } from "lucide-react";
import { zjCreate, zjGet, zjDownloadUrl } from "../../lib/api";

const POLL_INTERVAL_MS = 3000;

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

/**
 * ZipJobModal — Steuert kompletten Lifecycle eines ZIP-Job-Downloads.
 *
 * Props:
 *   open: boolean — Modal sichtbar?
 *   onClose: () => void
 *   payload: { scope, kategorie?, magic_link_token?, include_intern? }
 *   title: string — z. B. „Alles als ZIP" oder „Kategorie 06 als ZIP"
 *
 * Flow:
 *   1) Beim Öffnen wird POST /zip-jobs gefeuert → job_id
 *   2) Alle 3 s GET /zip-jobs/{id} bis status=ready|failed
 *   3) Bei ready: Download-Button (window.location = download-url) + Schließen
 */
export default function ZipJobModal({ open, onClose, payload, title = "ZIP wird erstellt" }) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [autoDownload, setAutoDownload] = useState(true);
  const pollRef = useRef(null);
  const startedRef = useRef(false);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Reset state + start job
  useEffect(() => {
    if (!open) {
      stopPolling();
      setJob(null);
      setError(null);
      startedRef.current = false;
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;

    (async () => {
      try {
        const created = await zjCreate(payload);
        setJob(created);
        // Poll
        pollRef.current = setInterval(async () => {
          try {
            const next = await zjGet(created.job_id);
            setJob(next);
            if (next.status === "ready" || next.status === "failed" || next.status === "expired") {
              stopPolling();
              if (next.status === "ready" && autoDownload) {
                // Direkter Download
                window.location.href = zjDownloadUrl(next.job_id);
              }
            }
          } catch (e) {
            // Continue polling, transient errors
          }
        }, POLL_INTERVAL_MS);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "ZIP-Job konnte nicht gestartet werden");
      }
    })();

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const pct = job?.progress_pct ?? 0;
  const isReady = job?.status === "ready";
  const isFailed = job?.status === "failed" || job?.status === "expired" || !!error;
  const isBuilding = job?.status === "queued" || job?.status === "building" || job?.status === "assembling";

  return (
    <>
      <div onClick={isBuilding ? undefined : onClose} className="fixed inset-0 bg-navy/40 z-[80]" />
      <div
        data-testid="zip-job-modal"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(540px,95vw)] max-h-[90vh] overflow-y-auto bg-white border border-navy/15 z-[90] shadow-xl"
      >
        <header className="border-b border-navy/10 px-6 py-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <Package size={22} className="text-gold mt-1 shrink-0" />
            <div className="min-w-0">
              <div className="pb-eyebrow">ZIP-Job</div>
              <h3 className="font-display font-semibold text-navy text-lg sm:text-xl mt-1 tracking-tight">
                {title}
              </h3>
              {job?.total_files != null && (
                <p className="text-xs font-sans text-navy/65 mt-1 tabular-nums">
                  {job.total_files} Dateien · ca. {fmtSize(job.size_bytes_estimated)}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-navy/55 hover:text-navy p-1 shrink-0 disabled:opacity-30"
            aria-label="Schließen"
            disabled={isBuilding}
            title={isBuilding ? "Bitte warten, bis ZIP fertig ist" : "Schließen"}
            data-testid="zip-job-close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="border-l-[3px] border-l-tot bg-tot/5 px-4 py-3 text-xs font-sans text-navy" data-testid="zip-job-error">
              <AlertCircle size={14} className="inline mr-1" /> {error}
            </div>
          )}

          {isBuilding && (
            <div data-testid="zip-job-building">
              <div className="flex items-center gap-2 text-sm font-sans text-navy/75 mb-3">
                <Loader2 size={14} className="animate-spin text-gold" />
                {job?.status === "queued"
                  ? "In Warteschlange…"
                  : job?.status === "assembling"
                  ? "Finaler Zusammenbau…"
                  : `ZIP wird erstellt — ${job?.processed_files ?? 0} von ${job?.total_files ?? "?"} Dateien…`}
              </div>
              <div className="h-2.5 bg-navy/8 overflow-hidden">
                <div
                  className="h-full bg-navy transition-all duration-500"
                  style={{ width: `${Math.max(2, pct)}%` }}
                  data-testid="zip-job-progress-bar"
                />
              </div>
              <div className="text-[11px] font-sans text-navy/55 mt-2 tabular-nums">
                {pct}% · je nach Volumen 1–3 Minuten
              </div>
            </div>
          )}

          {isReady && (
            <div data-testid="zip-job-ready">
              <div className="flex items-center gap-2 text-sm font-sans text-emerald-900 mb-3">
                <CheckCircle2 size={16} className="text-emerald-700" />
                ZIP ist fertig — {fmtSize(job.size_bytes)}
              </div>
              <a
                href={zjDownloadUrl(job.job_id)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm uppercase tracking-wider bg-navy text-white hover:bg-navy/85 transition-colors"
                data-testid="zip-job-download-btn"
              >
                <Download size={14} /> Jetzt herunterladen
              </a>
              <div className="text-[11px] font-sans text-navy/55 mt-2">
                Verfügbar für 60 Minuten, danach wird die Datei automatisch gelöscht.
              </div>
            </div>
          )}

          {isFailed && !error && (
            <div className="border-l-[3px] border-l-tot bg-tot/5 px-4 py-3 text-xs font-sans text-navy" data-testid="zip-job-failed">
              <AlertCircle size={14} className="inline mr-1" />
              {job?.status === "expired"
                ? "Job ist abgelaufen — bitte erneut anfordern."
                : `Fehler beim ZIP-Bau: ${job?.error || "Unbekannt"}`}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-navy/10 flex justify-end">
          <button
            onClick={onClose}
            disabled={isBuilding}
            className="pb-btn-ghost disabled:opacity-30"
            data-testid="zip-job-cancel"
          >
            {isReady ? "Schließen" : "Abbrechen"}
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Hook für einfache Verwendung:
 *   const { openZip, modal } = useZipJob();
 *   <button onClick={() => openZip({scope:"all"}, "Kompletter Datenraum")}>ZIP</button>
 *   {modal}
 */
export function useZipJob() {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);
  const [title, setTitle] = useState("");

  const openZip = useCallback((pl, ti = "ZIP wird erstellt") => {
    setPayload(pl);
    setTitle(ti);
    setOpen(true);
  }, []);

  const modal = (
    <ZipJobModal
      open={open}
      onClose={() => setOpen(false)}
      payload={payload || { scope: "all" }}
      title={title}
    />
  );

  return { openZip, modal };
}
